import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCalendarDate } from '@/lib/utils/date';
import { useFlowsheet, usePatientFlowsheet } from '@/features/emr/api/pregnancies-api';
import type { FlowsheetVisit, PregnancyOut } from '@/features/emr/types';

// Context-view template UUIDs (from migration 056_seed_pregnancies_and_context_views.py)
const TPL_OB_ID = 'd4e5f6a7-0004-4000-8000-000000000001';
const TPL_GYN_ID = 'd4e5f6a7-0004-4000-8000-000000000002';
const TPL_GENERAL_ID = 'd4e5f6a7-0004-4000-8000-000000000003';
const TPL_NEWBORN_ID = 'd4e5f6a7-0004-4000-8000-000000000004';

interface FlowsheetColumn {
  label: string;
  render: (visit: FlowsheetVisit) => ReactNode;
}

function formatDate(visit: FlowsheetVisit): string {
  return formatCalendarDate(visit.encounter_date);
}

function tc(visit: FlowsheetVisit): Record<string, unknown> {
  return (visit.clinical_context?.template_context ?? {}) as Record<string, unknown>;
}

function vitals(visit: FlowsheetVisit): Record<string, unknown> {
  return (visit.clinical_context?.vitals ?? {}) as Record<string, unknown>;
}

function textField(keyTc: string, keyVitals?: string): (v: FlowsheetVisit) => ReactNode {
  return (visit) => {
    const t = tc(visit);
    if (t[keyTc] != null) return String(t[keyTc]);
    if (keyVitals) {
      const vt = vitals(visit);
      if (vt[keyVitals] != null) return String(vt[keyVitals]);
    }
    return '—';
  };
}

function formatGA(visit: FlowsheetVisit): string {
  const t = tc(visit);
  const weeks = t.gestational_age_weeks ?? t.ga_weeks;
  const days = t.gestational_age_days ?? t.ga_days ?? 0;
  if (weeks == null) return '—';
  return `${weeks}w${days}d`;
}

function formatFHR(visit: FlowsheetVisit): string {
  const t = tc(visit);
  const low = t.fhr_low as number | undefined;
  const high = t.fhr_high as number | undefined;
  if (low == null && high == null) {
    const single = t.fhr as string | number | undefined;
    return single != null ? String(single) : '—';
  }
  if (low != null && high != null) return `${low}–${high}`;
  return String(low ?? high ?? '—');
}

function formatApgar(visit: FlowsheetVisit): string {
  const t = tc(visit);
  const a1 = t.apgar_1min;
  const a5 = t.apgar_5min;
  if (a1 == null && a5 == null) return '—';
  return `${a1 ?? '—'}/${a5 ?? '—'}`;
}

const COLUMN_MAP: Record<string, FlowsheetColumn[]> = {
  [TPL_GENERAL_ID]: [
    { label: 'Date', render: formatDate },
    { label: 'BP', render: textField('blood_pressure', 'blood_pressure') },
    { label: 'HR', render: textField('heart_rate', 'heart_rate') },
    { label: 'Temp', render: textField('temperature', 'temperature') },
    { label: 'Weight', render: textField('weight', 'weight') },
    { label: 'SpO2', render: textField('oxygen_saturation', 'oxygen_saturation') },
  ],
  [TPL_OB_ID]: [
    { label: 'Date', render: formatDate },
    { label: 'GA', render: formatGA },
    { label: 'BP', render: textField('bp', 'blood_pressure') },
    { label: 'Weight', render: textField('weight', 'weight') },
    { label: 'FH', render: textField('fundal_height') },
    { label: 'FHR', render: formatFHR },
    { label: 'FM', render: textField('fetal_movement') },
    { label: 'Protein', render: textField('urine_protein') },
  ],
  [TPL_GYN_ID]: [
    { label: 'Date', render: formatDate },
    { label: 'LMP', render: textField('last_menstrual_period') },
    { label: 'Cycle', render: textField('cycle_regularity') },
    { label: 'Contraception', render: textField('contraception') },
    { label: 'Last Pap', render: textField('last_pap_date') },
    { label: 'HPV', render: textField('hpv_status') },
    { label: 'Mammogram', render: textField('mammogram_date') },
  ],
  [TPL_NEWBORN_ID]: [
    { label: 'Date', render: formatDate },
    { label: 'Birth Wt', render: textField('birth_weight_grams') },
    { label: "APGAR 1'/5'", render: formatApgar },
    { label: 'Delivery', render: textField('delivery_method') },
    { label: 'Feeding', render: textField('feeding_method') },
    { label: 'Hearing', render: textField('hearing_screen') },
    { label: 'Metabolic', render: textField('metabolic_screen') },
    { label: 'Bilirubin', render: textField('bilirubin_level') },
  ],
};

function PregnancySummary({ pregnancy }: { pregnancy: PregnancyOut }) {
  return (
    <Card className="mb-3">
      <CardContent className="grid grid-cols-2 gap-x-4 gap-y-1 p-3 text-xs">
        <div>
          <span className="text-muted-foreground">EDD:</span> {pregnancy.edd ? formatCalendarDate(pregnancy.edd) : '—'}
        </div>
        <div>
          <span className="text-muted-foreground">GTPAL:</span> {pregnancy.gtpal_display}
        </div>
        <div>
          <span className="text-muted-foreground">Fetuses:</span> {pregnancy.fetus_count}
        </div>
        <div>
          <span className="text-muted-foreground">VBAC:</span> {pregnancy.vbac ? 'Yes' : 'No'}
        </div>
        {pregnancy.problems && (
          <div className="col-span-2">
            <span className="text-muted-foreground">Problems:</span> {pregnancy.problems}
          </div>
        )}
        <div className="col-span-2">
          <Badge variant="outline" className="text-[10px]">
            {pregnancy.status}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

interface FlowsheetTabProps {
  patientId: string;
  pregnancyId: string | null;
  contextViewTemplateId: string | null;
  currentEncounterId?: string;
}

function Table({
  columns,
  visits,
  currentEncounterId,
}: {
  columns: FlowsheetColumn[];
  visits: FlowsheetVisit[];
  currentEncounterId?: string;
}) {
  if (visits.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground">No prior visits recorded.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/50">
            {columns.map((col) => (
              <th key={col.label} className="whitespace-nowrap px-2 py-1.5 text-left font-medium text-muted-foreground">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visits.map((visit) => {
            const isCurrent = currentEncounterId && visit.encounter_id === currentEncounterId;
            return (
              <tr key={visit.encounter_id} className={cn('border-b last:border-b-0', isCurrent && 'bg-primary/5')}>
                {columns.map((col) => (
                  <td key={col.label} className="whitespace-nowrap px-2 py-1.5">
                    {col.render(visit)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function FlowsheetTab({ patientId, pregnancyId, contextViewTemplateId, currentEncounterId }: FlowsheetTabProps) {
  const useOBPregnancyPath = contextViewTemplateId === TPL_OB_ID && !!pregnancyId;

  const pregnancyQuery = useFlowsheet(useOBPregnancyPath ? pregnancyId! : undefined);
  const patientQuery = usePatientFlowsheet(
    useOBPregnancyPath ? undefined : patientId,
    useOBPregnancyPath ? undefined : contextViewTemplateId,
  );

  const isLoading = useOBPregnancyPath ? pregnancyQuery.isLoading : patientQuery.isLoading;

  if (!contextViewTemplateId) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">Select a context view to see the flow sheet.</p>
    );
  }

  const columns = COLUMN_MAP[contextViewTemplateId];
  if (!columns) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Flow sheet is not available for this context view.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (useOBPregnancyPath) {
    const data = pregnancyQuery.data;
    if (!data) {
      return <p className="py-8 text-center text-sm text-muted-foreground">No flow sheet data available.</p>;
    }
    return (
      <div className="space-y-3">
        <PregnancySummary pregnancy={data.pregnancy} />
        <Table columns={columns} visits={data.visits} currentEncounterId={currentEncounterId} />
      </div>
    );
  }

  const visits = patientQuery.data?.visits ?? [];
  return (
    <div className="space-y-3">
      <Table columns={columns} visits={visits} currentEncounterId={currentEncounterId} />
    </div>
  );
}
